import React, {useReducer, useRef, useImperativeHandle, forwardRef, MouseEvent} from 'react'
import {emit} from 'messenger/socketio/socketEmit.js'
import {Address} from 'server/functions/addresses/addresses.types'
import {Alert, Button, Card, Colors, IconButton, Modal, P, Popup, Row, Stack} from '@components/UI/index.js'
import SelectCustomer from '@components/customers/SelectCustomer.js'
import {formSpacing, AddressSearch, FormRow, FormStack, Input, RadioGroup, Toggle} from '@components/UI/forms/index.js'
import {Select} from '../components/UI/index.js'
import PostalCodeAutocomplete from '@components/UI/forms/PostalCodeAutocomplete.jsx'
import {OrderAddress, AddressType, Customer, CustomersObject, Collo, BrandingObject, Reseller, Route, Order, AddressInAddressBook, Shift} from 'types/types.js'
import ScanFrame from './ScanFrame.js'
import {SelectCustomerRef} from '@components/customers/SelectCustomer.js'
import Countries from 'src/utils/Countries.js'
import dbCodes from 'server/dbCodes.js'
import {normalize} from '@utils/normalize.js'
import type {PopupRef} from '@components/UI/overlays/Popup.js'
import questionOptions from '../utils/questionOptions.js'
import t from 'src/helpers/Languages.js'

interface AddAddressModalProps {
    reseller: Reseller
    customers: CustomersObject
    addressBook?: AddressInAddressBook[]
    route: Route & {
        orders: Record<string, Order>
    }
    branding: BrandingObject
    shift: Shift
    onClose?: () => void
    onColliAdded?: (colli: Collo[]) => void
}

interface NameSuggestion {
    value: string
    label: string
    address: Partial<OrderAddress>
}

interface AddAddressModalState {
    modalIsOpen: boolean
    customerId: string
    customerDisabled: boolean
    addressTypeDisabled: boolean
    address: OrderAddress
    options: string[]
    colli: Collo[]
    addAnotherAddress: boolean
    scanning: boolean
    loading: boolean
    error: string
    validation: {
        street: boolean
    }
}

export interface AddAddressModalRef {
    open: (customerId?: string, addressType?: AddressType) => void
}

const AddAddressModal = forwardRef<AddAddressModalRef, AddAddressModalProps>((props, ref) => {
    const {reseller, customers, addressBook = [], route, branding, shift, onClose, onColliAdded} = props

    const selectCustomerRef = useRef<SelectCustomerRef>(null)
    const popupRef = useRef<PopupRef>(null)
    // Set synchronously, so a second submit during a slow request is ignored even before the loading state has rendered
    const submittingRef = useRef(false)

    const initialState: AddAddressModalState = {
        modalIsOpen: false,
        customerId: '',
        customerDisabled: false,
        addressTypeDisabled: false,
        address: {
            type: 'delivery',
            name: '',
            attention: '',
            street: '',
            nr: '',
            addition: '',
            street2: '',
            postalCode: '',
            city: '',
            state: '',
            country: 'NL',
            instructions: '',
            phone: '',
            email: ''
        },
        options: [],
        colli: [],
        addAnotherAddress: localStorage.getItem('addAnotherAddress') === 'true',
        scanning: false,
        loading: false,
        error: '',
        validation: {
            street: false
        }
    }

    const [state, setState] = useReducer((state: AddAddressModalState, newState: Partial<AddAddressModalState>) => ({...state, ...newState}), structuredClone(initialState))

    useImperativeHandle(ref, () => ({open}))

    const resolveDefaultOptions = (customer: Customer, addressType: AddressType) => {
        let options: string[] = []

        if (addressType === 'pickup') {
            options = reseller.settings.orders.defaultPickupOptions

            if (customer.settings.orders.defaultPickupOptions && Array.isArray(customer.settings.orders.defaultPickupOptions)) {
                options = customer.settings.orders.defaultPickupOptions
            } else if (customer.settings.orders.defaultPickupOptions === 'none') {
                options = []
            }
        }

        if (addressType === 'delivery') {
            options = reseller.settings.orders.defaultDeliveryOptions

            if (customer.settings.orders.defaultDeliveryOptions && Array.isArray(customer.settings.orders.defaultDeliveryOptions)) {
                options = customer.settings.orders.defaultDeliveryOptions
            } else if (customer.settings.orders.defaultDeliveryOptions === 'none') {
                options = []
            }
        }

        return options
    }

    const setDefaultOptions = (customer: Customer, address: OrderAddress = state.address) => {
        onChangeOptions(resolveDefaultOptions(customer, address.type), address)
    }

    const open = (customerId?: string, addressType?: AddressType) => {
        const customer = customers[customerId] || (Object.values(customers)[0] as Customer)
        const address = addressType ? {...state.address, type: addressType} : state.address

        if (customer) {
            setDefaultOptions(customer, address)
        } else if (addressType) {
            setState({address})
        }

        setState({modalIsOpen: true, customerId, customerDisabled: !!customerId, addressTypeDisabled: !!addressType})
    }

    const close = () => {
        setState(structuredClone(initialState))
        if (onClose) onClose()
    }

    const onCloseModal = () => {
        if (submittingRef.current) return
        close()
    }

    const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()

        if (submittingRef.current || !validate()) {
            return
        }

        submittingRef.current = true
        setState({loading: true, error: ''})

        try {
            const existingAddress = route.addresses.find((address) => {
                return (
                    !address.hasArrived &&
                    address.customerId === state.customerId &&
                    address.type === state.address.type &&
                    (!state.address.name || normalize(address.name.toLowerCase()) === normalize(state.address.name.toLowerCase())) &&
                    address.street === state.address.street &&
                    address.nr === state.address.nr &&
                    address.addition === state.address.addition &&
                    address.postalCode === state.address.postalCode &&
                    address.city === state.address.city &&
                    address.country === state.address.country
                )
            })

            const colli = state.colli?.length === 0 ? [{barcode: 'no barcode', status: 'athub'}] : state.colli
            const addedColli = structuredClone(colli)

            if (existingAddress) {
                if (!await popupRef.current.open(t('addressAlreadyExists'), t('addressAlreadyExistsInRouteText'))) {
                    setState({loading: false})
                    return
                }

                const orderStatus = route.orders?.[existingAddress.orderId]?.status

                if (orderStatus === dbCodes.status.onderweg()) {
                    for (const c of colli) {
                        c.status = 'outfordelivery'
                    }
                }

                await emit('orders.addColli', {
                    id: existingAddress.orderId,
                    colli
                })

                popupRef.current.close()
            } else {
                await emit('orders.createMessengerOrder', {
                    customerId: state.customerId,
                    routeId: route._id,
                    address: state.address,
                    colli
                })
            }

            onColliAdded?.(addedColli as Collo[])

            if (state.addAnotherAddress) {
                const customer = customers[state.customerId] || (Object.values(customers)[0] as Customer)
                const nextAddress = {...structuredClone(initialState.address), type: state.addressTypeDisabled ? state.address.type : initialState.address.type}

                setState({...structuredClone(initialState), addAnotherAddress: true, customerId: state.customerId, modalIsOpen: true, customerDisabled: state.customerDisabled, addressTypeDisabled: state.addressTypeDisabled, address: nextAddress})

                if (customer) {
                    setDefaultOptions(customer, nextAddress)
                }
            } else {
                close()
            }
        } catch (err) {
            setState({loading: false, error: err.message})
        } finally {
            submittingRef.current = false
        }
    }

    const handleScan = (barcode: string) => {
        const colli = [...state.colli]
        colli.push({barcode, status: 'athub'})
        setState({colli})
    }

    const onChangeOptions = (values: string[], baseAddress: OrderAddress = state.address) => {
        const address: OrderAddress = {
            ...baseAddress,
            gpsRequired: false,
            signatureRequired: false,
            signatureNeighbors: false,
            nameRequired: false,
            statedAddressOnly: false,
            mailboxDelivery: false,
            refrigerated: false,
            scanRequired: false,
            imageRequired: false,
            minimumAge: false,
            idCheck: false,
            customOptions: []
        }

        values.forEach((key) => {
            if (key === 'minimumAge +16') {
                address.minimumAge = 16
            } else if (key === 'minimumAge +18') {
                address.minimumAge = 18
            } else if (key === 'minimumAge +21') {
                address.minimumAge = 21
            } else if (key in dbCodes.pickupAndDeliveryOptions) {
                address[key] = true
            } else {
                address.customOptions.push(key)
            }
        })

        setState({address, options: values})
    }

    const validate = () => {
        let id = ''
        const validation = {
            street: false
        }

        if (!state.address.street) {
            validation.street = true
            id = 'street'
        }

        const customerValid = selectCustomerRef.current?.validate()

        if (!customerValid) {
            selectCustomerRef.current?.focus()
            id = 'customer'
        }

        if (id) {
            document.getElementById(id)?.focus()
            setState({validation})
            return false
        }

        return true
    }

    const resolveNameSuggestions = (): NameSuggestion[] => {
        const query = normalize(state.address.name.toLowerCase())

        if (!query) {
            return []
        }

        const customerSuggestions: NameSuggestion[] = Object.values(customers)
            .filter((customer) => !customer.isArchived && normalize(customer.name.toLowerCase()).indexOf(query) > -1)
            .map((customer) => ({
                value: customer._id,
                label: customer.name,
                address: {
                    name: customer.name,
                    street: customer.address?.street,
                    nr: customer.address?.nr,
                    addition: customer.address?.addition,
                    street2: customer.address?.street2,
                    postalCode: customer.address?.postalCode,
                    city: customer.address?.city,
                    state: customer.address?.state,
                    country: customer.address?.country,
                    phone: customer.phone,
                    email: customer.email
                }
            }))

        const addressBookSuggestions: NameSuggestion[] = addressBook
            .filter((entry) => entry.name && normalize(entry.name.toLowerCase()).indexOf(query) > -1)
            .map((entry) => ({
                value: entry._id,
                label: entry.name,
                address: {
                    name: entry.name,
                    attention: entry.attention,
                    street: entry.street,
                    nr: entry.nr,
                    addition: entry.addition,
                    street2: entry.street2,
                    postalCode: entry.postalCode,
                    city: entry.city,
                    state: entry.state,
                    country: entry.country,
                    phone: entry.phone,
                    email: entry.email,
                    instructions: entry.instructions
                }
            }))

        return [...customerSuggestions, ...addressBookSuggestions].slice(0, 9)
    }

    const nameSuggestions = resolveNameSuggestions()

    return (
        <Modal
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: 'fit-content',
                maxHeight: '95vh',
                marginTop: 'auto',
                marginBottom: 0,
                borderRadius: '8px 8px 0 0'
            }}
            title={t('addAddress')}
            show={state.modalIsOpen}
            onClose={onCloseModal}
            closeOnEscape={false}
        >
            {!state.scanning &&
                <>
                    <Stack style={{flex: 1, overflowY: 'scroll', width: '100%'}}>
                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Row style={{alignItems: 'flex-end', gap: formSpacing.tight}}>
                                <div style={{flex: 1}}>
                                    <SelectCustomer
                                        value={state.customerId}
                                        customers={customers}
                                        onChange={({customer}: {customer: Customer}) => {
                                            setState({customerId: customer._id})
                                            setDefaultOptions(customer)
                                        }}
                                        disabled={state.customerDisabled}
                                        ref={selectCustomerRef}
                                    />
                                </div>

                                {state.customerDisabled &&
                                    <IconButton
                                        onClick={async () => {
                                            if (!shift?.completed) {
                                                const confirmed = await popupRef.current.openBoolean(t('attention'), t('changeCustomerActiveShiftText', shift?.name), questionOptions())
                                                if (!confirmed) return
                                            }
                                            setState({customerDisabled: false})
                                        }}
                                    >
                                        <i className='mdi mdi-pencil' />
                                    </IconButton>
                                }
                            </Row>

                            <RadioGroup
                                value={state.address.type}
                                disabled={state.addressTypeDisabled}
                                options={[
                                    {value: 'pickup', label: t('pickupAction')},
                                    {value: 'delivery', label: t('deliverAction')}
                                ]}
                                onChange={(event) => {
                                    const addressType = event.target.value as AddressType
                                    const customer = customers[state.customerId]
                                    const address = {...state.address, type: addressType}

                                    if (customer) {
                                        onChangeOptions(resolveDefaultOptions(customer, addressType), address)
                                    } else {
                                        onChangeOptions([], address)
                                    }
                                }}
                            />

                            <Input
                                id='name'
                                label={t('name')}
                                value={state.address.name}
                                suggestions={nameSuggestions}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setState({address: {...state.address, name: event.target.value}})
                                }}
                                onChooseSuggestion={(event: {target: {value: string}}) => {
                                    const suggestion = nameSuggestions.find((s) => s.value === event.target.value)

                                    if (suggestion) {
                                        setState({address: {...state.address, ...suggestion.address}})
                                    }
                                }}
                                blurOnChooseSuggestion={true}
                            />

                            <PostalCodeAutocomplete
                                address={state.address}
                                onChange={(address: Address) => {
                                    setState({address: {...state.address, ...address}})
                                }}
                            />

                            <AddressSearch
                                id='street'
                                label={t('street')}
                                onChange={(address: Address) => {
                                    setState({address: {...state.address, ...address}})
                                }}
                                address={state.address}
                                showStreetOnly={true}
                                blurOnChooseSuggestion={true}
                                isInvalid={state.validation.street}
                            />

                            <FormRow>
                                <Input
                                    id='city'
                                    label={t('city')}
                                    value={state.address.city}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setState({address: {...state.address, city: event.target.value}})
                                    }}
                                />

                                <Select
                                    style={{width: 100}}
                                    label={t('country')}
                                    value={state.address.country || 'NL'}
                                    showValue={true}
                                    options={Countries.map((country) => ({value: country.code2, title: country.name}))}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setState({address: {...state.address, country: event.target.value}})
                                    }}
                                />
                            </FormRow>

                            <Input
                                label={t('instructions')}
                                value={state.address.instructions}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setState({address: {...state.address, instructions: event.target.value}})
                                }}
                            />

                            {state.address.type === 'pickup' &&
                                <Select
                                    label={t('options')}
                                    values={state.options}
                                    options={[
                                        ...Object.entries(dbCodes.pickupOptions).map(([value, title]) => ({value, title})),
                                        ...(reseller?.settings.orders?.customOptions || []).map((option) => ({
                                            value: option.id,
                                            title: option.name,
                                            icon: 'mdi mdi-circle',
                                            iconColor: option.color,
                                            showIconInValue: true
                                        }))
                                    ]}
                                    onChange={(event) => onChangeOptions(event.target.values)}
                                    readOnly={true}
                                />
                            }

                            {state.address.type === 'delivery' &&
                                <Select
                                    label={t('options')}
                                    values={state.options}
                                    options={[
                                        ...Object.entries(dbCodes.pickupAndDeliveryOptions).map(([value, title]) => ({value, title})),
                                        ...(reseller.settings.orders?.customOptions || []).map((option) => ({
                                            value: option.id,
                                            title: option.name,
                                            icon: 'mdi mdi-circle',
                                            iconColor: option.color,
                                            showIconInValue: true
                                        }))
                                    ]}
                                    onChange={(event) => onChangeOptions(event.target.values)}
                                    readOnly={true}
                                />
                            }

                            <Button
                                style={{width: '100%'}}
                                variant='outline-white'
                                icon='mdi mdi-barcode-scan'
                                label={t('scanBarcode')}
                                onClick={() => setState({scanning: !state.scanning})}

                            />

                            {state.colli.length > 0 &&
                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                    {state.colli.map((collo, index) => {
                                        return (
                                            <Card shadow key={`collo-${index}`} style={{height: 'fit-content', width: '100%', marginBottom: 3}}>
                                                <P>{collo.barcode}</P>
                                            </Card>
                                        )
                                    })}
                                </div>
                            }

                            <Toggle
                                label={t('addAnotherAddress')}
                                checked={state.addAnotherAddress}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    localStorage.setItem('addAnotherAddress', event.target.checked.toString())
                                    setState({addAnotherAddress: event.target.checked})
                                }}
                            />
                        </FormStack>
                    </Stack>

                    {state.error && <Alert variant='error' style={{marginBottom: 12}}>{state.error}</Alert>}

                    <Button
                        style={{width: '100%', marginBottom: 12}}
                        size='xl'
                        variant='solid'
                        color={branding?.color || Colors.brandColor}
                        label={t('confirm')}
                        loading={state.loading}
                        onClick={handleSubmit}
                    />

                    <Button
                        style={{width: '100%'}}
                        variant='outline-white'
                        label={t('close')}
                        disabled={state.loading}
                        onClick={onCloseModal}
                    />
                </>
            }

            {state.scanning &&
                <Stack style={{flex: 1}}>
                    <ScanFrame onScan={handleScan} />

                    <Stack style={{flex: 1, overflowY: 'scroll', marginBottom: 24, minHeight: 200, padding: 6}}>
                        {state.colli.map((collo, index) => {
                            return (
                                <Card shadow key={`collo-${index}`} style={{height: 'fit-content', width: '100%', marginBottom: 3}}>
                                    <P>{collo.barcode}</P>
                                </Card>
                            )
                        })}
                    </Stack>

                    <Button
                        variant='solid'
                        color={branding?.color || Colors.brandColor}
                        style={{width: '100%'}}
                        size='xl'
                        label={t('finishScanning')}
                        icon='mdi mdi-check'
                        onClick={() => setState({scanning: false})}
                    />
                </Stack>
            }

            <Popup ref={popupRef} />
        </Modal>
    )
})

AddAddressModal.displayName = 'AddAddressModal'

export default AddAddressModal
